import SEO from '../components/SEO';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Award, Briefcase, ArrowLeft, Mail, Phone } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamMemberPage = () => {
  const { staffId } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMember = async () => {
      try {
        const response = await axios.get(`${API}/staff/${staffId}`);
        setMember(response.data);
      } catch {
        // Error handled silently - UI shows not found
      } finally {
        setLoading(false);
      }
    };
    fetchMember();
  }, [staffId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Team member not found</h1>
          <Link to="/team" className="text-blue-600 hover:underline" data-testid="back-to-team-link">
            Back to Our Team
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="team-member-page">
      <SEO
        title={member.name}
        description={`${member.name}, ${member.title} at SDORA Wellbeing LLC in Pikesville, MD. ${member.years_experience}+ years of clinical experience. Specialties include ${(member.specialties || []).slice(0, 3).join(', ')}.`}
        path={`/team/${staffId}`}
      />
      <Navigation />

      {/* Back Link */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link
          to="/team"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-blue-600 transition-colors"
          data-testid="back-to-team"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Our Team
        </Link>
      </div>

      {/* Profile Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid md:grid-cols-5 gap-10">
          {/* Photo Column */}
          <div className="md:col-span-2">
            <div className="rounded-2xl overflow-hidden shadow-lg sticky top-8">
              <img
                src={member.image_url}
                alt={member.name}
                className="w-full aspect-[3/4] object-cover"
                data-testid="member-photo"
              />
            </div>
          </div>

          {/* Details Column */}
          <div className="md:col-span-3">
            <h1
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2"
              data-testid="member-name"
            >
              {member.name}
            </h1>
            <p className="text-lg text-blue-600 font-semibold mb-6" data-testid="member-title">
              {member.title}
            </p>

            {/* Credentials */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Award className="w-5 h-5 text-blue-600" />
                <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Credentials</h2>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="member-credentials">
                {member.credentials.map((cred) => (
                  <span
                    key={cred}
                    className="text-sm bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg font-medium"
                  >
                    {cred}
                  </span>
                ))}
              </div>
            </div>

            {/* Experience */}
            <div className="flex items-center gap-2 mb-8 text-gray-700">
              <Briefcase className="w-5 h-5 text-blue-600" />
              <span className="font-medium" data-testid="member-experience">
                {member.years_experience}+ years of clinical experience
              </span>
            </div>

            {/* Bio */}
            <div className="mb-8">
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">About</h2>
              <p className="text-gray-700 leading-relaxed text-base" data-testid="member-bio">
                {member.bio}
              </p>
            </div>

            {/* Specialties */}
            <div className="mb-8">
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">Areas of Expertise</h2>
              <div className="grid grid-cols-2 gap-2" data-testid="member-specialties">
                {member.specialties.map((specialty) => (
                  <div
                    key={specialty}
                    className="flex items-center gap-2 bg-gray-50 px-4 py-3 rounded-lg"
                  >
                    <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />
                    <span className="text-sm text-gray-800 font-medium">{specialty}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Ready to get started?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Schedule an appointment with {member.name.split(',')[0]} today.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="tel:8777725150"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
                  data-testid="member-call-btn"
                >
                  <Phone className="w-4 h-4" />
                  Call (877) 772-5150
                </a>
                <Link
                  to="/services/referrals"
                  className="inline-flex items-center gap-2 bg-white text-blue-600 border border-blue-200 px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-50 transition-colors"
                  data-testid="member-referral-btn"
                >
                  <Mail className="w-4 h-4" />
                  Submit Referral
                </Link>
              </div>
            </div>

            {/* Approach & Practice Info */}
            <div className="mt-8 space-y-6">
              <div>
                <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">Approach to Care</h2>
                <p className="text-gray-700 leading-relaxed text-base">
                  As a member of the SDORA Wellbeing clinical team, {member.name.split(',')[0]} provides
                  evidence-based, trauma-informed care in a supportive and culturally responsive environment.
                  Treatment is individualized to each client's needs, goals, and strengths, with a focus on
                  building resilience and improving overall well-being.
                </p>
              </div>

              <div>
                <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">Practice Information</h2>
                <div className="bg-gray-50 rounded-xl p-5 text-sm text-gray-700 space-y-2">
                  <p><span className="font-semibold text-gray-900">Location:</span> 1700 Reisterstown Rd, Suite 109, Pikesville, MD 21208</p>
                  <p><span className="font-semibold text-gray-900">Office Hours:</span> Monday – Friday, 9:00 AM – 5:00 PM</p>
                  <p><span className="font-semibold text-gray-900">Phone:</span> (877) 772-5150</p>
                  <p><span className="font-semibold text-gray-900">Insurance:</span> Medicaid, Medicare, and most major insurance plans accepted</p>
                  <p><span className="font-semibold text-gray-900">Accreditation:</span> SDORA Wellbeing is CARF accredited</p>
                </div>
              </div>

              {(member.specialties || []).length > 0 && (
                <div>
                  <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">Related Services</h2>
                  <p className="text-gray-700 leading-relaxed text-sm mb-3">
                    {member.name.split(',')[0]}'s areas of expertise connect to several of the mental health
                    and behavioral health programs offered at SDORA Wellbeing, including outpatient mental health
                    clinic (OMHC) services and psychiatric rehabilitation (PRP) programs.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Link to="/services/outpatient-mental-health" className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg font-medium hover:bg-blue-100 transition-colors">Outpatient Therapy</Link>
                    <Link to="/services/psychiatric-rehabilitation" className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg font-medium hover:bg-blue-100 transition-colors">PRP Services</Link>
                    <Link to="/services/medication-management" className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg font-medium hover:bg-blue-100 transition-colors">Medication Management</Link>
                    <Link to="/services/comprehensive-assessment" className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg font-medium hover:bg-blue-100 transition-colors">Psychiatric Assessment</Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TeamMemberPage;
